import Button from "components/Button/Button"
import ModalCustom from "components/ModalCustom"
import { useBoolean, useString, useNumber } from "helpers/hooks"
import { SelectedUser, ShareKeyProject } from "../ShareKeyOrganizationPage"
import { Avatar } from "components/Avatar"
import { useEffect, useState } from "react"
import forge from "node-forge"
import { includes } from "lodash"
import { createEncryptionKeysMiddleware } from "pages/projects/services/api"
import { KeyEncryptionType } from "types"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"

interface Props {
  isOpen: boolean
  isAutoProceed?: boolean
  projectKeys: ShareKeyProject[]
  selectedUsers: SelectedUser[]
  isShareAllKeys: boolean
  onDone: () => void
  onCloseModal: () => void
}

interface UserProgress {
  [userId: string]: number
}

export const ShareKeyProjectModal = (props: Props) => {
  const {
    onCloseModal,
    projectKeys,
    selectedUsers,
    onDone,
    isShareAllKeys,
    isOpen,
    isAutoProceed,
  } = props

  const isProcessing = useBoolean(false)
  const [userProgress, setUserProgress] = useState<UserProgress>({})
  const [isCompleted, setIsCompleted] = useState(false)
  const allUserProgress = useNumber(0)
  const [selectedUserProgress, setSelectedUserProgress] =
    useState<SelectedUser | null>(null)
  useEffect(() => {
    if (isOpen && isAutoProceed && selectedUsers.length && projectKeys.length) {
      onProceed()
    }
  }, [isOpen, isAutoProceed])

  const onProceed = async () => {
    isProcessing.setValue(true)
    const initialProgress: UserProgress = {}
    selectedUsers.forEach((user) => {
      initialProgress[user.id] = 0
    })
    setUserProgress(initialProgress)
    setIsCompleted(false)
    allUserProgress.setValue(0)
    setSelectedUserProgress(null)

    try {
      if (isShareAllKeys) {
        // Share all keys mode - process all users and all keys
        const totalSteps = selectedUsers.length * projectKeys.length
        let completedSteps = 0

        for (const user of selectedUsers) {
          const publicKeyFromPem = forge.pki.publicKeyFromPem(user.publicKey)
          setSelectedUserProgress(user)
          for (const projectKey of projectKeys) {
            try {
              if (includes(user.projectKeyIds, projectKey.projectId)) {
                completedSteps++
                allUserProgress.setValue((completedSteps / totalSteps) * 100)
                continue
              }

              const encryptedKey = publicKeyFromPem.encrypt(
                projectKey.projectKey,
                "RSA-OAEP"
              )

              // Retry logic for createEncryptionKeysMiddleware
              let retryCount = 0
              let success = false

              while (retryCount < 3 && !success) {
                const result = await createEncryptionKeysMiddleware(
                  [
                    {
                      encrypted_key: encryptedKey,
                      relation_id: projectKey.projectId,
                      type: KeyEncryptionType.ProjectShareKey,
                      user_id: user.id,
                    },
                  ],
                  true
                )
                if (result) {
                  success = true
                } else {
                  retryCount++
                  if (retryCount >= 3) {
                    console.error(
                      `Error sharing key for user ${user.id} after 3 retries:`
                    )
                    break
                  }
                }
              }
              completedSteps++
              allUserProgress.setValue((completedSteps / totalSteps) * 100)
            } catch (error) {
              console.error(`Error sharing key for user ${user.id}:`, error)
              completedSteps++
              allUserProgress.setValue((completedSteps / totalSteps) * 100)
            }
          }
        }

        // Ensure progress reaches 100% at the end
        allUserProgress.setValue(100)
        toast(
          <LabelNotificationPage
            messenger="Share key project successfully!"
            type="success"
          />
        )
        setIsCompleted(true)
        return
      }
      // Individual user mode - process each user separately
      const userPromises = selectedUsers.map(async (user) => {
        let currentProgress = 0
        const totalSteps = projectKeys.length
        const publicKeyFromPem = forge.pki.publicKeyFromPem(user.publicKey)

        // Process each project key for this user
        for (let i = 0; i < projectKeys.length; i++) {
          const projectKey = projectKeys[i]
          try {
            if (includes(user.projectKeyIds, projectKey.projectId)) {
              currentProgress += 100 / totalSteps
              setUserProgress((prev) => ({
                ...prev,
                [user.id]: Math.min(currentProgress, 100),
              }))
              continue
            }

            const encryptedKey = publicKeyFromPem.encrypt(
              projectKey.projectKey,
              "RSA-OAEP"
            )

            // Retry logic for createEncryptionKeysMiddleware
            let retryCount = 0
            let success = false

            while (retryCount < 3 && !success) {
              const result = await createEncryptionKeysMiddleware(
                [
                  {
                    encrypted_key: encryptedKey,
                    relation_id: projectKey.projectId,
                    type: KeyEncryptionType.ProjectShareKey,
                    user_id: user.id,
                  },
                ],
                true
              )
              if (result) {
                success = true
              } else {
                retryCount++
                if (retryCount >= 3) {
                  console.error(
                    `Error sharing key for user ${user.id} after 3 retries:`
                  )
                  break
                }
              }
            }
            currentProgress += 100 / totalSteps
            setUserProgress((prev) => ({
              ...prev,
              [user.id]: Math.min(currentProgress, 100),
            }))
          } catch (error) {
            console.error(`Error sharing key for user ${user.id}:`, error)
            currentProgress += 100 / totalSteps
            setUserProgress((prev) => ({
              ...prev,
              [user.id]: Math.min(currentProgress, 100),
            }))
          }
        }

        // Ensure progress reaches 100% at the end
        setUserProgress((prev) => ({
          ...prev,
          [user.id]: 100,
        }))
      })

      // Wait for all users to complete
      await Promise.all(userPromises)
      toast(
        <LabelNotificationPage
          messenger="Share key project successfully!"
          type="success"
        />
      )
      if (isAutoProceed) {
        onDone()
      }
      setIsCompleted(true)
    } catch (error) {
      console.error("Error processing users:", error)
      toast(
        <LabelNotificationPage
          messenger="Share key project failed!"
          type="error"
        />
      )
      onCloseModal()
    }
  }

  const renderProcessUI = () => {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-3 my-[16px]">
          {isShareAllKeys ? (
            <div className="flex flex-col">
              {selectedUserProgress ? (
                <div className="flex items-center">
                  <Avatar
                    width="24px"
                    height="24px"
                    name={
                      selectedUserProgress.fullname ||
                      selectedUserProgress.email
                    }
                    avatar={selectedUserProgress?.avatar}
                    textcolor="white"
                    contentsize="11px"
                  />
                  <p className="font-semibold overflow-hidden text-ellipsis text-[#111111] text-[13px] leading-[22px] ml-2">
                    {selectedUserProgress?.fullname || "N/A"}
                  </p>
                  <div className="h-[4px] min-w-[4px] mx-2 bg-[#7A7A7A] rounded-full" />
                  <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                    {selectedUserProgress.email}
                  </p>
                </div>
              ) : null}

              <div className="flex items-center mt-2">
                <div className="flex items-center flex-1">
                  <div className="w-[320px] bg-[#E4E4E4] rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ease-out`}
                      style={{
                        width: `${allUserProgress.value}%`,
                        backgroundColor:
                          allUserProgress.value === 100 ? "#56A012" : "#F7AC1B",
                      }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center justify-end ml-4">
                  <p className="text-[14px] text-[#111111] leading-[22px] font-semibold">
                    {Math.round(allUserProgress.value)}%
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              {selectedUsers.map((user) => {
                const currentProgress = userProgress[user.id] || 0
                return (
                  <div key={user.id} className="flex flex-col">
                    <div className="flex items-center">
                      <Avatar
                        width="24px"
                        height="24px"
                        name={user.fullname || user.email}
                        avatar={user?.avatar}
                        textcolor="white"
                        contentsize="11px"
                      />
                      <p className="font-semibold overflow-hidden text-ellipsis text-[#111111] text-[13px] leading-[22px] ml-2">
                        {user?.fullname || "N/A"}
                      </p>
                      <div className="h-[4px] min-w-[4px] mx-2 bg-[#7A7A7A] rounded-full" />
                      <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                        {user.email}
                      </p>
                    </div>
                    <div className="flex items-center mt-2">
                      <div className="flex items-center flex-1">
                        <div className="w-[320px] bg-[#E4E4E4] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ease-out`}
                            style={{
                              width: `${currentProgress}%`,
                              backgroundColor:
                                currentProgress === 100 ? "#56A012" : "#F7AC1B",
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end ml-4">
                        <p className="text-[14px] text-[#111111] leading-[22px] font-semibold">
                          {Math.round(currentProgress)}%
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between mt-[16px]">
          <Button
            widthBtn={"100%"}
            disabled={!isCompleted}
            title="Done"
            onClick={onDone}
          />
        </div>
      </div>
    )
  }

  const renderBody = () => {
    if (isProcessing.value) {
      return renderProcessUI()
    }

    return (
      <div className="flex flex-col ">
        <p className="text-[14px] text-[#222222] leading-[24px]">
          This action will share <strong>all relevant keys</strong> associated
          with the project with the selected user. Make sure you trust this user
          and confirm before proceeding.
        </p>
        <p className="text-[14px] text-[#222222] leading-[24px]">
          This action cannot be undone!
        </p>
        <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between mt-[16px]">
          <Button
            widthBtn={"100%"}
            title="Cancel"
            colorBtn="white"
            onClick={onCloseModal}
          />
          <Button widthBtn={"100%"} title="Proceed" onClick={onProceed} />
        </div>
      </div>
    )
  }
  return (
    <ModalCustom
      handleChangeButton={isProcessing.value ? undefined : () => onCloseModal}
      label={isProcessing.value ? "Sharing keys..." : "Warning"}
      widthModal={406}
      styleHeaderModal={{
        height: "auto",
        paddingTop: 12,
        alignItems: "start",
      }}
      bodyChildren={renderBody()}
    />
  )
}
